<template>
  <div
    class="forced-colors:b"
    :class="[brandClasses.container, { 'justify-center': variant }]"
    data-test-id="vf-notification"
    :style="brandStyles?.container"
  >
    <vf-icon
      v-if="type"
      :class="[
        brandClasses.icon,
        type === 'error' ? brandClasses.iconError : '',
        type === 'info' ? brandClasses.iconInfo : '',
        type === 'success' ? brandClasses.iconSuccess : '',
      ]"
      :name="type"
      :size="sizeType"
    />
    <span
      :id="contentId"
      aria-live="assertive"
      :class="[
        brandClasses.message,
        {
          'flex-1': !variant,
          'mr-6': variant && dismissible && closeVariant !== 'label',
          'mr-12': variant && dismissible && closeVariant === 'label',
          ...(brandClasses.messageError && { [brandClasses.messageError]: type === 'error' }),
        },
      ]"
      role="alert"
    >
      <slot />
    </span>
    <base-button
      v-if="(type !== 'error' || dismissibleOnError) && dismissible"
      :aria-labelledby="`${contentId} ${dismissId}`"
      class="h-8"
      :class="[
        brandClasses.close,
        { 'absolute right-0 mr-2 md:mr-4': variant },
      ]"
      data-test-id="vf-notification-close"
      @click="$emit('close')"
    >
      <span :id="dismissId" :class="closeVariant === 'label' ? 'subtitle-5 underlined' : 'sr-only'">
        {{ $t.dismiss }}
      </span>
      <vf-icon v-if="closeVariant === 'icon'" name="close" :size="sizeClose" />
    </base-button>
  </div>
</template>

<script lang="ts" setup>
import type { BaseNotification } from '#types/notification'
import { NotificationConfigContextKey } from './context'

const { variant, dismissible = true } = defineProps<Omit<BaseNotification, 'message'> & {
  /**
   * Defines content position
   */
  variant?: 'center'
  /**
   * Defines close button visibility
   * If true close button becomes visible and emits 'close' event on click
   * Ignored if a type is equal to 'error'
   */
  dismissible?: boolean
}>()

defineEmits<{
  /**
   * Emits when close button is clicked
   */
  close: []
}>()
const contentId = useId()
const dismissId = useId()

const injectedConfig = inject(NotificationConfigContextKey)

const {
  brandClasses: configBrandClasses,
  brandStyles: configBrandStyles,
  closeIconSize,
  closeVariant,
  dismissibleOnError,
  iconSize
} = useAppConfig().components.vf.notification
const { $viewport } = useNuxtApp()

const brandClasses = computed(() => ({ ...configBrandClasses, ...injectedConfig?.brandClasses }))
const brandStyles = computed(() => ({ ...configBrandStyles, ...injectedConfig?.brandStyles }))

const sizeClose = computed(() => isObject(closeIconSize) ? closeIconSize[$viewport.breakpoint] : closeIconSize)
const sizeType = computed(() => isObject(iconSize) ? iconSize[$viewport.breakpoint] : iconSize)
</script>
